import { Button, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineContentCopy from '~icons/ic/baseline-content-copy'
import MaterialSymbolsDownload from '~icons/material-symbols/download'
import type {
  RankingContentTypeValues,
  RankingRangeTypeValues,
} from 'src/components/Ranking/ranking-utils'
import type { Artwork } from 'src/services/apollo/generated/graphql'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { getMediaUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { isSafari } from 'src/utils/userAgent'
import { openArtworkShareActionSuccessDialog } from '../ArtworkDetail/ArtworkShare/ArtworkShareDialog'
import {
  convertBlobToPng,
  downloadFile,
  getImageDownloadFileExt,
  getImageDownloadFilename,
} from '../ArtworkDetail/utils'
import { useRankingSnapshot } from './useRankingSnapshot'

export function RankingBottomActionBar(props: {
  artwork: Artwork
  rankingOpts: {
    filterType: RankingRangeTypeValues
    type: RankingContentTypeValues
    rankNumber: number
  }
}) {
  const { t } = useTranslation(['common'])
  const { artwork, rankingOpts } = props
  const isAnimated = isAnimatedArtwork(artwork)

  const title = t('common:ranking.title.share-dialog.bottom', {
    filterType: t(`common:ranking.filter-type.${rankingOpts.filterType}`),
    type: t(`common:ranking.${rankingOpts.type}.label`),
  })

  const { snapshot, isReady, Portal } = useRankingSnapshot(
    artwork.media!,
    rankingOpts,
    title,
  )

  const [loadingDownload, downloadArtwork] = useLoadingCallback(async () => {
    track('share_image_dl_click', { scene: 'ranking' })

    const imageExt = getImageDownloadFileExt({ artwork, media: artwork.media })
    const imageFileName = `${getImageDownloadFilename({ artwork })}.${imageExt}`

    // skip snapshot if media is animated
    const url = isAnimated
      ? getMediaUrl(artwork.videoMediaId, 'orig')
      : await snapshot()

    if (url) {
      try {
        await downloadFile(url, imageFileName, { skipConversion: isAnimated })
        track('share_image_dl_dialog_show', { scene: 'ranking' })
      } catch (err: any) {
        toast.error(
          t('artwork:share.watermark-dialog.notice.failed-to-download') +
            ': ' +
            err.message,
        )
      }
    }
  }, [artwork, isAnimated, snapshot])

  const [loadingCopy, copyArtwork] = useLoadingCallback(async () => {
    track('share_copy_image_click', { scene: 'ranking' })
    const fileType = 'image/png'
    // convert non png images to png, then write to clipboard
    try {
      const getBlob = async () => {
        const fileUrl = await snapshot()

        if (!fileUrl) throw new Error('File URL not available')

        let blob: Blob | File | undefined
        const origBlob = await fetch(fileUrl).then(r => r.blob())
        if (origBlob.type === 'image/png') {
          blob = origBlob
        } else {
          blob = await convertBlobToPng(origBlob, 'image.png')
        }
        if (!blob) throw new Error('image not exists')

        return blob
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          [fileType]: getBlob(), // NOTE: Safari requires clipboard item to be a Promise<Blob>
        }),
      ])
      track('share_copy_image_dialog_show', { scene: 'ranking' })
      openArtworkShareActionSuccessDialog(t, { type: 'copy' })
    } catch (e: any) {
      toast.error(
        t('artwork:share.watermark-dialog.notice.failed-to-copy') +
          ': ' +
          e.message,
      )
    }
  }, [artwork, snapshot])

  // FIXME: Can't make Safari to work with watermark
  // I gave up
  if (isSafari()) return null

  return (
    <>
      <div
        className="flex items-center absolute bottom-0 px-2 py-3 w-full gap-4 min-w-[120px] justify-end"
        style={{ fontSize: '16px' }}
      >
        {/* Hide copy for animated media */}
        {!isAnimated && (
          <Button
            className="min-w-0 w-10 h-10 rounded-full"
            disabled={loadingCopy || !isReady}
            onClick={copyArtwork}
          >
            {loadingCopy ? (
              <CircularProgress className="text-primary" size={20} />
            ) : (
              <IcBaselineContentCopy width={20} height={20} />
            )}
          </Button>
        )}
        <Button
          disabled={loadingDownload || (!isAnimated && !isReady)}
          className="min-w-0 w-10 h-10 rounded-full"
          onClick={downloadArtwork}
        >
          {loadingDownload ? (
            <CircularProgress className="text-primary" size={20} />
          ) : (
            <MaterialSymbolsDownload fontSize={20} />
          )}
        </Button>
      </div>
      {Portal}
    </>
  )
}
